import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, AlertCircle, Sparkles, ArrowRight } from "lucide-react";
import { Logo, LogoMark } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Markdown } from "@/components/Markdown";
import { Seo } from "@/components/Seo";
import { getSharedConversation } from "@/lib/api";

/**
 * Public read-only view of a shared Sabzy AI conversation. No auth required.
 * Doubles as a growth landing page — the CTA at the bottom invites the viewer
 * to start their own free chat.
 */
export default function SharedChat() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    setLoading(true);
    setErr("");
    getSharedConversation(token)
      .then((d) => setData(d))
      .catch((e) => setErr(e.response?.data?.detail || "This shared chat is no longer available."))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (err) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background p-6 text-center">
        <Seo title="Shared chat — Sabzy AI" description="A read-only Sabzy AI conversation." />
        <div className="grid h-12 w-12 place-items-center rounded-full bg-destructive/10 text-destructive">
          <AlertCircle className="h-5 w-5" />
        </div>
        <div className="text-base font-medium text-foreground" data-testid="share-error-text">{err}</div>
        <Link
          to="/"
          className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition hover:opacity-90"
          data-testid="share-error-home-link"
        >
          Try Sabzy AI for free →
        </Link>
      </div>
    );
  }

  const conv = data.conversation || {};
  const messages = data.messages || [];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Seo
        title={`${conv.title || "Shared chat"} — Sabzy AI`}
        description={`A read-only Sabzy AI conversation${conv.author_name ? ` by ${conv.author_name}` : ""}.`}
        noindex={true}
      />

      {/* Header */}
      <header className="sticky top-0 z-10 border-b border-border/40 bg-background/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3 sm:px-6">
          <Link to="/" className="flex items-center gap-2.5" data-testid="share-header-brand">
            <LogoMark size={26} />
            <span className="flex items-baseline leading-none">
              <span className="text-[18px] font-semibold tracking-[-0.025em]" style={{ fontFamily: "'Outfit', sans-serif" }}>sabzy</span>
              <span className="text-[18px] font-semibold tracking-[-0.025em] text-primary" style={{ fontFamily: "'Outfit', sans-serif" }}>.ai</span>
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/auth"
              className="hidden rounded-lg bg-primary px-3.5 py-1.5 text-xs font-medium text-primary-foreground transition hover:opacity-90 sm:inline-block"
              data-testid="share-header-cta"
            >
              Try free
            </Link>
            <ThemeToggle />
          </div>
        </div>
      </header>

      {/* Title block */}
      <section className="mx-auto max-w-3xl px-4 pt-10 pb-4 sm:px-6" data-testid="share-title-block">
        <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-border/60 bg-secondary/40 px-2.5 py-1 text-[11px] font-medium text-muted-foreground">
          <Sparkles className="h-3 w-3 text-primary" /> Shared by {conv.author_name || "Sabzy User"}
        </div>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl" data-testid="share-title">
          {conv.title || "Shared conversation"}
        </h1>
      </section>

      {/* Messages */}
      <main className="mx-auto max-w-3xl space-y-6 px-4 pb-12 sm:px-6">
        {messages.length === 0 && (
          <div className="rounded-xl border border-border/60 bg-secondary/30 p-6 text-center text-sm text-muted-foreground" data-testid="share-empty">
            This conversation is empty.
          </div>
        )}
        {messages.map((m, i) => {
          const isUser = m.role === "user";
          return (
            <div key={m.id || `share-${i}`} className={`flex ${isUser ? "justify-end" : "justify-start"}`} data-testid={`share-msg-${i}`}>
              <div
                className={`max-w-[88%] rounded-2xl border px-4 py-3 text-sm leading-relaxed shadow-sm ${
                  isUser
                    ? "border-primary/30 bg-primary/10 text-foreground"
                    : "border-border/60 bg-card text-foreground"
                }`}
              >
                {!isUser && m.model_label && (
                  <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground" data-testid={`share-msg-model-${i}`}>
                    {m.model_label}
                  </div>
                )}
                {isUser ? (
                  <div className="whitespace-pre-wrap">{m.content}</div>
                ) : (
                  <div className="prose-sm max-w-none">
                    <Markdown content={m.content || ""} />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </main>

      {/* Growth CTA */}
      <section className="border-t border-border/40 bg-secondary/20" data-testid="share-cta-section">
        <div className="mx-auto max-w-3xl px-4 py-10 text-center sm:px-6">
          <div className="mb-2 text-xs font-medium uppercase tracking-wider text-primary">
            Built with Sabzy AI
          </div>
          <h2 className="mb-2 text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
            Have your own conversation in 10 seconds
          </h2>
          <p className="mb-6 text-sm text-muted-foreground">
            ChatGPT, Claude & Gemini · racing mode · voice cloning · website builder — all in one chat.
          </p>
          <Link
            to="/auth"
            className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition hover:opacity-90"
            data-testid="share-cta-button"
          >
            Start free <ArrowRight className="h-4 w-4" />
          </Link>
          <div className="mt-3 text-[11px] text-muted-foreground">No credit card required</div>
        </div>
      </section>
    </div>
  );
}
